// src/components/DiscountCard.jsx
import { Trash2, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import OfferingsSelector from './OfferingsSelector';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getOfferings } from '../utils/api';

export default function DiscountCard({
  form,
  onChange,
  kind,
  businessId,
  errors = {},
  onDelete,
  currencyMeta = { code: 'USD', digits: 2, symbol: '$', sample: '$1,234.56' },
}) {
  const [open, setOpen] = useState(!form?.id);

  function h(e) {
    const { name, value, type, checked } = e.target;
    onChange({ ...form, [name]: type === 'checkbox' ? checked : value });
  }

  // Dates
  const startDate = form.start_date ? new Date(form.start_date) : null;
  const endDate = form.end_date ? new Date(form.end_date) : null;

  // ---- Products metadata (for reservations perks labels) ----
  const [productIndex, setProductIndex] = useState({});
  useEffect(() => {
    let mounted = true;
    getOfferings('products', businessId).then((data) => {
      const arr = Array.isArray(data) && data.length && data[0]?.id ? data : (
        Array.isArray(data) && data[0]?.items
          ? data[0].items // if a grouped shape ever comes back
          : []
      );
      const idx = {};
      arr.forEach(p => { idx[p.id] = p.name; });
      if (mounted) setProductIndex(idx);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [businessId]);

  function setDiscountPercent(product_id, val) {
    const n = Number(val);
    const arr = Array.isArray(form.discounted_products) ? [...form.discounted_products] : [];
    const idx = arr.findIndex(dp => dp.product_id === product_id);
    if (idx >= 0) {
      arr[idx] = { ...arr[idx], percent: n };
      onChange({ ...form, discounted_products: arr });
    }
  }

  function removeDiscounted(product_id) {
    const arr = Array.isArray(form.discounted_products) ? form.discounted_products.filter(dp => dp.product_id !== product_id) : [];
    onChange({ ...form, discounted_products: arr });
  }
  const discountedSelectedIds = useMemo(
    () => (Array.isArray(form.discounted_products) ? form.discounted_products.map(dp => dp.product_id) : []),
    [form.discounted_products]
  );

  function setDiscountedSelected(nextIds) {
    const free = new Set(form.free_products || []);
    const filtered = nextIds.filter(id => !free.has(id)); // prevent overlap
    const prev = Array.isArray(form.discounted_products) ? form.discounted_products : [];
    const map = new Map(prev.map(dp => [dp.product_id, dp.percent]));
    const next = filtered.map(id => ({ product_id: id, percent: map.get(id) ?? 10 }));
    onChange({ ...form, discounted_products: next });
  }
  function stepFromDigits(d) {
    if (!Number.isFinite(d) || d <= 0) return '1';
    // e.g. 2 -> "0.01", 3 -> "0.001"
    return '0.' + '0'.repeat(d - 1) + '1';
  }

  function clampDecimals(str, digits) {
    const s = String(str ?? '');
    if (!s.includes('.')) return s;
    const [i, f] = s.split('.');
    if (digits <= 0) return i;
    return `${i}.${(f || '').slice(0, digits)}`;
  }

  return (
    <div className="rounded-lg border border-gray-200">
      {/* Header button with inline trash (stop event so it doesn't toggle) */}
      <div className="flex w-full items-center justify-between bg-gray-50 px-4 py-3">
        <button
          className="flex-1 text-left"
          onClick={() => setOpen(!open)}
          type="button"
        >
          <span className="font-medium">{form.name || '(untitled)'}</span>
        </button>
        <button
          type="button"
          title="Delete"
          onClick={(e) => { e.stopPropagation(); onDelete?.(form.id); }}
          className="text-red-500 hover:text-red-700"
        >
          <Trash2 size={18} />
        </button>
        <ChevronDown
          className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`}
          size={20}
        />
      </div>

      {open && (
        <div className="space-y-6 p-4">
          {/* Name */}
          <div className="flex items-center gap-2">
            <label className="w-40 font-medium">Admin label</label>
            <div className="flex-1">
              <input
                name="name"
                value={form.name}
                onChange={h}
                className={`w-full rounded border px-2 py-1 ${errors.name ? 'border-red-500' : ''}`}
                required
              />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>
          </div>

          {/* Apply to all + scope */}
          <label className="flex items-center gap-2">
            <input type="checkbox" name="apply_all" checked={!!form.apply_all} onChange={h} />
            Apply to all {kind === 'reservations' ? 'offerings' : 'products'}
          </label>
          {!form.apply_all && (
            <>
              <OfferingsSelector
                kind={kind}
                selected={form.items || []}
                setSelected={items => onChange({ ...form, items })}
                disabled={false}
                businessId={businessId}
              />
              {errors.items && <p className="text-xs text-red-600">{errors.items}</p>}
            </>
          )}

          {/* Discount type & value */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <select
                name="percentage"
                value={form.percentage ? 'percentage' : 'fixed'}
                onChange={e => onChange({ ...form, percentage: e.target.value === 'percentage' })}
                className="rounded border px-2 py-1"
              >
                <option value="percentage">Percentage %</option>
                <option value="fixed">Fixed amount</option>
              </select>
            </label>
            <div>
              <div className="flex items-center gap-2">
                {!form.percentage && (
                  <span title={currencyMeta.code} className="text-gray-600">{currencyMeta.symbol}</span>
                )}
                <input
                  type="number"
                  name="value"
                  inputMode="decimal"
                  value={form.value}
                  step={form.percentage ? '1' : stepFromDigits(currencyMeta.digits)}
                  min="0"
                  max={form.percentage ? '100' : undefined}
                  onChange={(e) => {
                    if (form.percentage) return h(e);
                    const cleaned = clampDecimals(e.target.value, currencyMeta.digits);
                    onChange({ ...form, value: cleaned });
                  }}
                  onBlur={(e) => {
                    if (form.percentage) return;
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) {
                      const rounded = n.toFixed(Math.max(0, currencyMeta.digits));
                      onChange({ ...form, value: rounded });
                    }
                  }}
                  className={`w-40 rounded border px-2 py-1 ${errors.value ? 'border-red-500' : ''}`}
                  placeholder={form.percentage ? '0–100' : `e.g. ${currencyMeta.sample}`}
                />
              </div>
              {errors.value && <p className="mt-1 text-xs text-red-600">{errors.value}</p>}
              {!errors.value && !form.percentage && (
                <p className="mt-1 text-[11px] text-gray-500">
                  {currencyMeta.code}: {currencyMeta.digits} decimal{currencyMeta.digits === 1 ? '' : 's'} allowed
                </p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="flex flex-wrap gap-4">
            <label className="flex flex-col">
              <span className="font-medium">Start date</span>
              <DatePicker
                selected={startDate}
                onChange={date => onChange({ ...form, start_date: date ? date.toISOString() : '' })}
                className={`rounded border px-2 py-1 ${errors.start_date ? 'border-red-500' : ''}`}
                isClearable
                placeholderText="No start date"
                dateFormat="yyyy-MM-dd"
              />
              {errors.start_date && <p className="mt-1 text-xs text-red-600">{errors.start_date}</p>}
            </label>
            <label className="flex flex-col">
              <span className="font-medium">End date</span>
              <DatePicker
                selected={endDate}
                onChange={date => onChange({ ...form, end_date: date ? date.toISOString() : '' })}
                className={`rounded border px-2 py-1 ${errors.end_date ? 'border-red-500' : ''}`}
                isClearable
                placeholderText="No end date"
                dateFormat="yyyy-MM-dd"
              />
              {errors.end_date && <p className="mt-1 text-xs text-red-600">{errors.end_date}</p>}
            </label>
          </div>

          {/* Numbers */}
          <div className="grid gap-4 sm:grid-cols-3">
            <InputNum label="Min spend" name="min_spend" form={form} onChange={h} error={errors.min_spend} />
            <InputNum label="Max uses / client" name="max_client_uses" form={form} onChange={h} error={errors.max_client_uses} />
            <InputNum label="Max total uses" name="max_total_uses" form={form} onChange={h} error={errors.max_total_uses} />
          </div>

          {/* Other checkboxes */}
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" name="new_customer_only" checked={!!form.new_customer_only} onChange={h} />
              New customer only
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" name="exclusive" checked={!!form.exclusive} onChange={h} />
              Exclusive (cannot combine)
            </label>
          </div>

          {/* Coupon code */}
          <div className="flex items-center gap-2">
            <label className="w-40 font-medium">Coupon code</label>
            <div className="flex-1">
              <input
                name="coupon"
                value={form.coupon || ''}
                onChange={e => onChange({ ...form, coupon: e.target.value.toUpperCase() })}
                className={`w-full rounded border px-2 py-1 ${errors.coupon ? 'border-red-500' : ''}`}
                placeholder="Optional"
              />
              {errors.coupon && <p className="mt-1 text-xs text-red-600">{errors.coupon}</p>}
            </div>
          </div>

          {/* Reservations: Product perks (now with products like on Products tab) */}
          {kind === 'reservations' && (
            <>
              <h4 className="mt-2 font-semibold">Product perks (optional)</h4>

              {/* Free products multi-select */}
              <div>
                <div className="mb-1 font-medium">Free products</div>
                <OfferingsSelector
                  kind="products"
                  businessId={businessId}
                  selected={form.free_products || []}
                  setSelected={(ids) => {
                    // prevent selecting any that are currently discounted
                    const blocked = new Set(discountedSelectedIds);
                    onChange({ ...form, free_products: ids.filter(id => !blocked.has(id)) });
                  }}
                  disabled={false}
                  disabledIds={discountedSelectedIds} // NEW: disable any discounted items
                />
              </div>

              {/* Discounted products: select + per-product percent */}
              <div className="mt-4">
                <div className="mb-1 font-medium">Discounted products</div>
                <OfferingsSelector
                  kind="products"
                  businessId={businessId}
                  selected={discountedSelectedIds}
                  setSelected={setDiscountedSelected}
                  disabled={false}
                  disabledIds={form.free_products || []} // NEW: disable any free items
                />
                {errors.free_products && (
                  <p className="mt-1 text-xs text-red-600">{errors.free_products}</p>
                )}

                {/* Selected list with % inputs */}
                {Array.isArray(form.discounted_products) && form.discounted_products.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {form.discounted_products.map(dp => (
                      <div key={dp.product_id} className="flex items-center gap-3">
                        <span className="flex-1 truncate">
                          {productIndex[dp.product_id] || dp.product_id}
                        </span>
                        <label className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">%</span>
                          <input
                            type="number"
                            min={1}
                            max={100}
                            step={1}
                            value={dp.percent ?? 10}
                            onChange={e => setDiscountPercent(dp.product_id, e.target.value)}
                            className={`w-20 rounded border px-2 py-1 ${errors[`discounted_products.${dp.product_id}`] ? 'border-red-500' : ''}`}
                          />
                        </label>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => removeDiscounted(dp.product_id)}
                          title="Remove product"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    {/* Per-row errors */}
                    {form.discounted_products.map(dp => (
                      errors[`discounted_products.${dp.product_id}`] ? (
                        <p key={dp.product_id + ':err'} className="text-xs text-red-600">
                          {errors[`discounted_products.${dp.product_id}`]}
                        </p>
                      ) : null
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* helpers */
function InputNum({ label, name, form, onChange, min = 0, error }) {
  return (
    <label className="flex flex-col">
      <span className="font-medium">{label}</span>
      <input
        type="number"
        name={name}
        min={min}
        value={form[name] ?? 0}
        onChange={onChange}
        className={`rounded border px-2 py-1 ${error ? 'border-red-500' : ''}`}
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </label>
  );
}
